/**
 * Seats that win and lose together.
 *
 * Reading the `teams` declaration is the same job wherever it is done — the
 * standings group by side, and the table shows the side's running score — so it
 * is done once here rather than once per screen. The lobby's turn timer and its
 * `duration` helper had already drifted into two character-identical copies
 * that agreed only because they were written together; this is the same shape
 * of mistake, caught before it was made twice.
 */

import type { MatchView } from '../net/match.js';
import type { GameLayoutWire } from '../net/session.js';

/** One team, as the view publishes it: the seats on it and what they have scored. */
export interface Side {
  seats: number[];
  score: number | null;
}

/**
 * The sides, or null when this game is played by individuals.
 *
 * Both halves have to be there and agree in length. A game that declared
 * `teams` and published one of them - or published a `score` array shorter than
 * its `seats` array - would otherwise render a side with nothing in it, which
 * reads as a bug in the game rather than in its view.
 *
 * Null rather than an empty list for "no teams", so that "this game has no
 * partnerships" and "this game has partnerships and published them wrong" stay
 * distinguishable at the call site.
 */
export function sidesOf(view: MatchView, layout: GameLayoutWire): Side[] | null {
  const spec = layout.teams;
  if (!spec) return null;

  const rawSeats = view.view[spec.seats];
  const rawScores = view.view[spec.score];
  if (!Array.isArray(rawSeats) || !Array.isArray(rawScores)) return null;
  if (rawSeats.length === 0 || rawSeats.length !== rawScores.length) return null;

  const sides: Side[] = [];
  for (let i = 0; i < rawSeats.length; i++) {
    const members = rawSeats[i];
    if (!Array.isArray(members)) return null;
    const score = rawScores[i];
    sides.push({
      seats: members.filter((seat): seat is number => typeof seat === 'number'),
      score: typeof score === 'number' ? score : null,
    });
  }
  return sides;
}

/**
 * Which side a seat is on, or null if it is on none of them.
 *
 * Needed because a game's `winner` is a SEAT even when the game is played by
 * sides - the seat that took the last trick belongs to a partnership, and it is
 * the partnership that won.
 */
export function sideOfSeat(sides: readonly Side[], seat: number): number | null {
  for (let i = 0; i < sides.length; i++) {
    if (sides[i]?.seats.includes(seat) === true) return i;
  }
  return null;
}

/**
 * What a side is called, when the game did not say.
 *
 * "Us" and "Them" only work from one seat's point of view, and the table screen
 * is shared - it is the one screen where nobody's point of view is the right
 * one. So sides are named by their members instead, which is also how people
 * say it out loud.
 */
export function sideName(side: Side, names: (seat: number) => string): string {
  return side.seats.map(names).join(' & ');
}
